/*
  Yotpo Loyalty "Rewards Sticky Bar" — the behaviour the app does not ship.

  Companion to section 10 of assets/wildfang.css. That section repairs the
  widget's LAYOUT; this file repairs its INTERACTION. Nothing here renders the
  widget — Yotpo's own bundle appends it into `header.header` ~1.6-2.3s after
  load (see the section 10 comment for why it lands there) — so every listener
  is delegated from `document` and survives the app re-rendering its own DOM
  when a customer signs in.

  Three gaps, all in the app, none fixable in CSS:

  1. KEYBOARD — WCAG 2.1.1 Level A. `.yotpo-banner-layout` carries
     role="button" tabindex="0" aria-expanded aria-controls and then binds a
     CLICK handler only. It is reachable by Tab and inoperable once you get
     there. We forward Enter and Space.

     The forward happens on KEYUP, not keydown, and that ordering is the whole
     trick. Yotpo moves focus INTO the drawer when it opens, so a keydown
     forward lets the trailing Enter keyup land on `.yotpo-drawer-sign-up-item`
     instead — whose own keyup handler navigates to the Shopify login page. The
     panel opens and is gone again inside one keypress. That is Blink-only
     (Gecko does not reproduce it), which is exactly the kind of bug that ships
     when a store owner verifies in one browser. Consuming the keyup here means
     the drawer does not exist yet when it fires.

  2. DISMISSAL. The app offers its own close X and nothing else — no Escape,
     no click-outside. Both are added below. Click-outside also settles a
     stacking collision the CSS could not: Dawn's mega-menu panel carries
     `.global-settings-popup` (base.css) at `z-index: -1` while Yotpo's
     wrapper computes `z-index: 10000`, so an open drawer would paint over a
     mega-menu panel opened underneath it. Closing on any outside click means
     the two can never be open at once.

  3. `--header-height` STALENESS. sections/header.liquid:345 measures
     `.section-header` in connectedCallback and on the 990px media change, and
     never again. Yotpo mounts long after both, so the custom property has been
     40px short of the real header on every page load — visible the moment
     `sticky_header_type` is changed away from "always", because hide() at :394
     currently early-returns before using it. A ResizeObserver calling the
     theme's own method keeps it honest.

  If Yotpo renames `.yotpo-banner-layout` or `#yotpo-drawer-layout`, every
  listener here simply stops matching and the widget reverts to the app's own
  click-only behaviour. It cannot leave a dead control behind.
*/
(function () {
  'use strict';

  var WIDGET = '.yotpo-widget-loyalty-floater-widget';
  var BANNER = '.yotpo-banner-layout';

  function bannerEl() {
    return document.querySelector(WIDGET + ' ' + BANNER);
  }

  /* ------------------------------------------------------------------
     PLACEMENT. Yotpo appends the widget to `header.header` and gives us no
     setting for anything finer (banner-position is header|footer, nothing
     else), so moving it into the icon cluster has to happen here.

     >=990px it goes first in `.header__icons`, taking the slot the country
     and language selectors used to occupy — those are switched off for the
     header in sections/header-group.json because the announcement bar renders
     its own pair (announcement-bar.liquid:139-160) and the two were duplicates.
     990px is exactly the width at which those selectors appeared, since both
     carry `small-hide medium-hide`, so the swap is like-for-like.

     Below 990px it stays where Yotpo put it — a centred pill in its own
     implicit header row. The icon cluster is only ~130px wide on a phone and
     the label alone is 229px, so it cannot live there.

     We remember the widget's original position rather than assuming it was
     last, so putting it back is exact.
     ------------------------------------------------------------------ */
  var desktop = window.matchMedia('(min-width: 990px)');
  var home = null;

  function place() {
    var widget = document.querySelector(WIDGET);
    var icons = document.querySelector('.header__icons');
    if (!widget || !icons) return;

    if (!home) home = {parent: widget.parentElement, next: widget.nextElementSibling};

    if (desktop.matches) {
      if (widget.parentElement !== icons) icons.insertBefore(widget, icons.firstElementChild);
    } else if (widget.parentElement !== home.parent) {
      home.parent.insertBefore(widget, home.next);
    }
  }

  if (desktop.addEventListener) desktop.addEventListener('change', place);
  else if (desktop.addListener) desktop.addListener(place);

  /* The widget mounts 1.6-2.3s after load, long after this deferred script
     runs, so watch for it and stop as soon as it lands. The timeout is a
     backstop for the case where the app never loads at all (blocked, consent
     not given) — an observer left running for the life of the page would fire
     on every DOM mutation the store makes. */
  if (!document.querySelector(WIDGET)) {
    var mo = new MutationObserver(function () {
      if (!document.querySelector(WIDGET)) return;
      place();
      mo.disconnect();
    });
    mo.observe(document.body, {childList: true, subtree: true});
    setTimeout(function () { mo.disconnect(); }, 20000);
  } else {
    place();
  }

  function isOpen(banner) {
    return !!banner && banner.getAttribute('aria-expanded') === 'true';
  }

  /* The app's toggle is its click handler; there is no API to call. */
  function toggle(banner) {
    banner.click();
  }

  /* Enter/Space, intercepted in the CAPTURE phase at document so the event
     never reaches Yotpo's own listener on the banner.

     Taking the key away from the app entirely — rather than forwarding
     alongside it — is what makes this behave the same in both engines. Yotpo
     turns out to bind Enter on KEYUP in Blink and not at all in Gecko, so a
     naive forward opens and immediately re-closes the drawer in Chrome (two
     toggles, measured) while working in Firefox. Consuming both keydown and
     keyup leaves exactly one toggle, ours, everywhere. It also removes the
     Blink navigation bug at the source: the app can no longer see the Enter
     keyup that it would otherwise deliver to `.yotpo-drawer-sign-up-item`
     after moving focus into the freshly opened drawer, which sent the user to
     the Shopify login page instead of showing them the panel.

     preventDefault on keydown is separately load-bearing: without it Space
     scrolls the page under the still-open popup. */
  function keyHandler(event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    if (!event.target.closest) return;

    var banner = event.target.closest(WIDGET + ' ' + BANNER);
    if (!banner) return;

    event.preventDefault();
    event.stopPropagation();

    if (event.type === 'keyup') toggle(banner);
  }

  document.addEventListener('keydown', keyHandler, true);
  document.addEventListener('keyup', keyHandler, true);

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var banner = bannerEl();
    if (!isOpen(banner)) return;
    toggle(banner);
    banner.focus();
  });

  document.addEventListener('click', function (event) {
    var banner = bannerEl();
    if (!isOpen(banner)) return;
    if (!event.target.closest || event.target.closest(WIDGET)) return;
    toggle(banner);
  });

  /* Re-measure --header-height whenever the header's own box changes — which
     includes the frame Yotpo mounts in. Guarded on both sides: the element is
     upgraded asynchronously, and setHeaderHeight only exists once it is. */
  if (typeof ResizeObserver === 'function') {
    var section = document.querySelector('.section-header');
    if (section) {
      new ResizeObserver(function () {
        var sticky = document.querySelector('sticky-header');
        if (sticky && typeof sticky.setHeaderHeight === 'function') sticky.setHeaderHeight();
      }).observe(section);
    }
  }
})();
